// Přehled trenérů nad vzorovým rozvrhem (6. 10. 2026, Láďa, schválený mockup v2).
//
// Místo rozbalovacího <select> karty trenérů s obsazeností: kolik slotů má týdně
// obsazených, kolik volných, putující klientky, nejbližší absence. Nad kartami
// souhrn kapacity celého týmu, filtry Lokalita a Role jako v Rezervacích
// (FiltrRadek, vícenásobný výběr) a řazení. Klik na kartu vybere trenéra, pod
// kartami se vykreslí jeho týdenní vzor (RozvrhVzor), jako dřív.
//
// Data: GET /api/trener-rozvrh-pattern?souhrn=1 (patternCompute pro všechny piloty
// najednou, lokalita z PG trainers, absence z hr_absence). Načtení trvá pár vteřin,
// proto kostra a výběr trenéra funguje i dřív, než souhrn dorazí.
//
// Mobil: filtry pod sebou, trenéři jako seznam s pruhem obsazenosti.
import React, { useMemo, useState } from "react";
import { CalendarRange, Check, Plane, Users } from "lucide-react";
import { useApiData } from "../lib/api.js";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { normalizujLokalitu } from "../lib/lokality.js";
import { FiltrRadek, PruhFiltru, Dlazdice, SkeletonRows } from "./ui.jsx";
import { Avatar } from "./VyberTrenera.jsx";

const RAZENI = [
  { key: "obsazenost", label: "Podle obsazenosti" },
  { key: "jmeno", label: "Podle jména" },
  { key: "volno", label: "Jen s volnem" },
];

function fmtCz(iso) { if (!iso) return ""; const [, m, d] = iso.split("-").map(Number); return `${d}. ${m}.`; }
function kratceLokalita(l) {
  const n = normalizujLokalitu(l);
  if (/^vt gym/i.test(n)) return "VT GYM";
  if (/sportovn/i.test(n)) return "Kbely";
  return n || "bez lokality";
}
function dnyText(dny) {
  if (!dny?.length) return "";
  const vsedni = ["Po", "Út", "St", "Čt", "Pá"];
  if (dny.length >= 5 && vsedni.every((d) => dny.includes(d))) return dny.length === 5 ? "Po až Pá" : dny.join(", ");
  return dny.join(", ");
}
function tonProcent(p) {
  if (p == null) return { text: "text-slate-500", bar: "bg-slate-300" };
  if (p >= 90) return { text: "text-amber-700", bar: "bg-amber-500" };
  return { text: "text-brand-700", bar: "bg-brand" };
}

export default function RozvrhVzorPrehled({ trainers = [], value, onChange }) {
  const uzka = useUzkaObrazovka();
  const { data, loading } = useApiData("/api/trener-rozvrh-pattern?souhrn=1");
  const [lokality, setLokality] = useState([]);
  const [razeni, setRazeni] = useState("obsazenost");

  // Karty: seznam trenérů z HR (jako dřív v <select>) plus ti ze souhrnu, které HR seznam
  // vynechává (manažer, který sám trénuje, třeba Michal). Kapacita týmu bez nich nedává smysl.
  const souhrnTreneru = useMemo(() => {
    const mapa = new Map((data?.treneri || []).map((t) => [String(t.jmeno).toLowerCase(), t]));
    const out = trainers.map((jmeno) => mapa.get(String(jmeno).toLowerCase()) || { jmeno, lokalita: "", dny: [], kapacita: null, putujici: 0, predrezervace: 0, absence: null });
    const mam = new Set(out.map((t) => String(t.jmeno).toLowerCase()));
    for (const t of (data?.treneri || [])) if (!mam.has(String(t.jmeno).toLowerCase())) out.push(t);
    return out;
  }, [trainers, data]);

  const lokalityItems = useMemo(() => {
    const set = new Set(souhrnTreneru.map((t) => kratceLokalita(t.lokalita)));
    return [...set].sort((a, b) => a.localeCompare(b, "cs")).map((l) => ({ key: l, label: l }));
  }, [souhrnTreneru]);

  const karty = useMemo(() => {
    let out = souhrnTreneru.filter((t) => !lokality.length || lokality.includes(kratceLokalita(t.lokalita)));
    if (razeni === "volno") out = out.filter((t) => (t.kapacita?.volno || 0) > 0);
    if (razeni === "jmeno") out.sort((a, b) => a.jmeno.localeCompare(b.jmeno, "cs"));
    else out.sort((a, b) => (b.kapacita?.procent ?? -1) - (a.kapacita?.procent ?? -1) || a.jmeno.localeCompare(b.jmeno, "cs"));
    return out;
  }, [souhrnTreneru, lokality, razeni]);

  const s = data?.souhrn;
  const nejplnejsi = useMemo(() => [...souhrnTreneru].filter((t) => t.kapacita).sort((a, b) => b.kapacita.procent - a.kapacita.procent)[0] || null, [souhrnTreneru]);
  const filtrZapnuty = lokality.length > 0 || razeni !== "obsazenost";

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <Dlazdice popisek="Volných slotů za týden" hodnota={s ? s.volno : "…"} ton="ok" doplnky={s ? <span className="text-xs font-semibold text-slate-500">z {s.celkem}, dopoledne {s.dopoledne}, odpoledne {s.odpoledne}</span> : null} />
        <Dlazdice popisek="Pro sales (po putujících)" hodnota={s ? s.pro_sales : "…"} ton="znacka" hodnotaTon="ok" />
        <Dlazdice popisek="Trenérů" hodnota={souhrnTreneru.length} ton="neutralni" doplnky={<span className="text-xs font-semibold text-slate-500">{souhrnTreneru.filter((t) => t.absence).length} s absencí do 30 dní</span>} />
        <Dlazdice popisek="Nejplnější" hodnota={nejplnejsi ? `${nejplnejsi.kapacita.procent} %` : "…"} ton="pozor" hodnotaTon={nejplnejsi && nejplnejsi.kapacita.procent >= 90 ? "pozor" : undefined} doplnky={nejplnejsi ? <span className="text-xs font-semibold text-slate-600 demo-sensitive">{nejplnejsi.jmeno}, volno {nejplnejsi.kapacita.volno}</span> : null} />
      </div>

      <PruhFiltru zapnuto={filtrZapnuty} onZrusit={() => { setLokality([]); setRazeni("obsazenost"); }}>
        <div className="grid gap-2 sm:gap-3 lg:grid-cols-2">
          <FiltrRadek stitek="Lokalita" popisVse="Všechny lokality" value={lokality} onChange={setLokality} items={lokalityItems} />
          <div className="flex flex-wrap items-center gap-1.5 lg:justify-end">
            {RAZENI.map((r) => <button key={r.key} type="button" className="cip" aria-pressed={razeni === r.key} onClick={() => setRazeni(r.key)}>{r.label}</button>)}
          </div>
        </div>
      </PruhFiltru>

      <div className="glass !p-3">
        <div className="mb-2 flex items-center justify-between px-1">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500"><Users className="h-4 w-4" />Trenéři{loading && !data ? ", počítám obsazenost" : ""}</div>
          {value && <button type="button" className="cip" onClick={() => onChange?.("")}>Zrušit výběr</button>}
        </div>
        {!trainers.length && <SkeletonRows rows={3} />}
        {trainers.length > 0 && karty.length === 0 && <div className="px-1 py-2 text-sm text-slate-500">Žádný trenér neodpovídá filtru.</div>}
        <div className={uzka ? "space-y-1.5" : "grid grid-cols-2 gap-2 xl:grid-cols-3 2xl:grid-cols-4"}>
          {karty.map((t) => {
            const vybrany = value && String(value).toLowerCase() === String(t.jmeno).toLowerCase();
            const k = t.kapacita;
            const ton = tonProcent(k?.procent);
            const popisDny = dnyText(t.dny);
            return uzka ? (
              <button key={t.jmeno} type="button" onClick={() => onChange?.(t.jmeno)} aria-pressed={!!vybrany}
                className={`flex min-h-[60px] w-full items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left ${vybrany ? "border-2 border-brand bg-brand-50" : "border-slate-200 bg-white"}`}>
                <Avatar name={t.jmeno} size="md" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2"><span className="truncate text-[15px] font-bold text-slate-900 demo-sensitive">{t.jmeno}</span><span className={`shrink-0 text-xs font-bold ${ton.text}`}>{k ? `${k.procent} %` : "…"}</span></span>
                  <span className={`mt-1 block h-1.5 overflow-hidden rounded-full ${vybrany ? "bg-white" : "bg-slate-100"}`}><span className={`block h-full ${ton.bar}`} style={{ width: `${Math.min(100, k?.procent || 0)}%` }} /></span>
                  <span className="mt-1 flex flex-wrap gap-1">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">{kratceLokalita(t.lokalita)}{popisDny && <><span className="h-3 w-px bg-slate-300" />{popisDny}</>}</span>
                    {k && <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${k.volno === 0 ? "bg-rose-50 text-rose-700" : k.volno <= 3 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{k.volno} {k.volno === 1 ? "volný" : k.volno < 5 ? "volné" : "volných"}</span>}
                    {t.putujici > 0 && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-700">{t.putujici} putující</span>}
                    {t.absence && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-700">absence {fmtCz(t.absence.od)} až {fmtCz(t.absence.do)}</span>}
                  </span>
                </span>
                {vybrany && <Check className="h-5 w-5 shrink-0 text-brand" />}
              </button>
            ) : (
              <button key={t.jmeno} type="button" onClick={() => onChange?.(t.jmeno)} aria-pressed={!!vybrany}
                className={`rounded-2xl border p-3 text-left transition ${vybrany ? "border-2 border-brand bg-brand-50" : "border-slate-200 bg-white hover:border-brand-300"}`}>
                <div className="flex items-center gap-2.5">
                  <Avatar name={t.jmeno} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-bold text-slate-900 demo-sensitive">{t.jmeno}</div>
                    {/* 6. 10. 2026 (Láďa): lokalita a dny jako pilulka, oddělovač svislá čára. */}
                    <div className="mt-0.5 inline-flex max-w-full items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11.5px] font-bold text-slate-600"><span className="truncate">{kratceLokalita(t.lokalita)}</span>{popisDny && <><span className="h-3 w-px bg-slate-300" /><span className="truncate">{popisDny}</span></>}</div>
                  </div>
                  {vybrany && <Check className="h-5 w-5 shrink-0 text-brand" />}
                </div>
                <div className="mt-2.5 flex items-center justify-between text-xs font-semibold">
                  <span className="text-slate-600">{k ? `Obsazeno ${k.obsazeno} z ${k.celkem}` : loading ? "Počítám…" : "Bez dat"}</span>
                  <span className={ton.text}>{k ? `${k.procent} %` : ""}</span>
                </div>
                <div className={`mt-1 h-2 overflow-hidden rounded-full ${vybrany ? "bg-white" : "bg-slate-100"}`}><div className={`h-full rounded-full ${ton.bar}`} style={{ width: `${Math.min(100, k?.procent || 0)}%` }} /></div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {k && <span className={`rounded-full px-2.5 py-0.5 text-[11.5px] font-bold ${k.volno === 0 ? "bg-rose-50 text-rose-700" : k.volno <= 3 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{k.volno} {k.volno === 1 ? "volný" : k.volno < 5 ? "volné" : "volných"}</span>}
                  {t.putujici > 0 && <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-[11.5px] font-bold text-blue-700">{t.putujici} putující</span>}
                  {t.predrezervace > 0 && <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11.5px] font-bold text-slate-600">{t.predrezervace} předrezervace</span>}
                  {t.absence && <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-[11.5px] font-bold text-rose-700"><Plane className="h-3 w-3" />{fmtCz(t.absence.od)} až {fmtCz(t.absence.do)}</span>}
                </div>
              </button>
            );
          })}
        </div>
        {!value && trainers.length > 0 && <div className="mt-2 flex items-center gap-1.5 px-1 text-xs text-slate-500"><CalendarRange className="h-3.5 w-3.5" />Klepni na trenéra, dole se ukáže jeho týdenní vzor.</div>}
      </div>
    </div>
  );
}
